import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';

import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
import { usePurchaseOrderStore } from '@/store/purchaseOrderStore';
import type { DistributionPortion } from '@/services/capacityCalendarService';
import { ASSIGNMENT_TYPE } from '@/types/capacityCalendar';
import type { CapacityCategory } from '@/types/capacityType';
import type { PurchaseOrder } from '@/types/purchaseOrders';

const SEARCH_DEBOUNCE_MS = 300;

export interface PoAssignmentContext {
  vendorId: string;
  capacityTypeId: string;
  typeCode: string;
  rowId: string;
  weekKey: string;
  remainingCapacity: number;
  assignedPoNumbers: string[];
  // The target allocation's capacity category. Undefined for callers that predate
  // molding support; treated the same as 'termination'. Molding types never carry
  // parsed counts, so a molding target always pre-fills 0 and allows submitting at
  // 0 — the same "not counted toward capacity" affordance non-capacity-relevant
  // POs already get.
  category?: CapacityCategory;
}

export interface WeekAllocationByType {
  rowId: string;
  remainedCapacity: number;
  assignedPoNumbers: string[];
  assignedQuantityByPoNumber: Record<string, number>;
}

export interface OverbookedType {
  typeCode: string;
  by: number;
}

export function usePoAssignment(context: () => PoAssignmentContext, onAssigned?: () => void) {
  const poStore = usePurchaseOrderStore();
  const calendarStore = useCapacityCalendarStore();
  // poNumber -> capacityTypeId -> quantity. Single-type/estimated/manual POs
  // use context().capacityTypeId as their sole key.
  const selected = ref<Record<string, Record<string, number>>>({});
  const submitting = ref(false);
  const pendingOverbook = ref<OverbookedType[] | null>(null);
  const manualQuantity = ref<number | null>(null);
  const manualNotes = ref('');
  const manualPoNumber = ref('');
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  // Purchase orders are always scoped to the context vendor — a PO from another
  // vendor is never assignable to this vendor's capacity — and to the context
  // capacity type, so the backend drops POs with nothing left to schedule there.
  function reload(): void {
    poStore.schedulableForType = context().capacityTypeId || undefined;
    void poStore.load(context().vendorId);
  }

  // Rows for the context vendor, used to resolve type codes <-> capacity
  // type ids and this week's sibling allocations for multi-type distribution.
  const vendorRows = computed(() => calendarStore.rows.filter((row) => row.vendorId === context().vendorId));

  const poByNumber = computed(() => new Map(poStore.items.map((po) => [po.poNumber, po])));

  // Non-capacity POs (no Finished Goods lines) may sit in a week at quantity 0;
  // a positive quantity is the explicit opt-in to consume capacity. A molding
  // target gets the same allowance for every PO — molding types have no parsed
  // counts, so no PO ever arrives with an auto-counted quantity there either.
  function allowsZeroQuantity(poNumber: string): boolean {
    return isNonCapacityPo(poNumber) || context().category === 'molding';
  }

  // A PO with no Finished Goods lines (Tools, Consumables, ...) is planning-only: it may sit
  // in a week for visibility but never consumes manufacturing capacity, so it gets no
  // quantity input at all. Distinct from allowsZeroQuantity, which also covers a molding
  // target — there a real PO may still be given a positive quantity.
  function isNonCapacityPo(poNumber: string): boolean {
    return poByNumber.value.get(poNumber)?.isCapacityRelevant === false;
  }

  const codeToTypeId = computed<Record<string, string>>(() =>
    Object.fromEntries(vendorRows.value.filter((row) => row.capacityTypeId && row.typeCode).map((row) => [row.typeCode, row.capacityTypeId as string]))
  );

  const typeIdToCode = computed<Record<string, string>>(() =>
    Object.fromEntries(vendorRows.value.filter((row) => row.capacityTypeId && row.typeCode).map((row) => [row.capacityTypeId as string, row.typeCode]))
  );

  // typeId -> that type's allocation for this week (only present when a
  // sibling row exists AND has a planned allocation for the week).
  const weekAllocationsByTypeId = computed<Record<string, WeekAllocationByType>>(() => {
    const result: Record<string, WeekAllocationByType> = {};
    vendorRows.value.forEach((row) => {
      if (!row.capacityTypeId) return;
      const allocation = calendarStore.allocations[row.id]?.[context().weekKey];
      if (!allocation) return;
      result[row.capacityTypeId] = {
        rowId: row.id,
        remainedCapacity: allocation.remainedCapacity,
        // A manual block's poNumber is a tracking reference, never a PO commitment —
        // counting it here would falsely mark the real PO as already assigned.
        assignedPoNumbers: allocation.purchaseOrders.flatMap((po) => (po.type !== ASSIGNMENT_TYPE.manual && po.poNumber ? [po.poNumber] : [])),
        assignedQuantityByPoNumber: quantityByPoNumber(allocation.purchaseOrders),
      };
    });
    return result;
  });

  // poNumber -> quantity currently assigned in one allocation. Manual blocks are excluded
  // for the same reason they are excluded from assignedPoNumbers: a block that merely
  // references a PO is not that PO's commitment.
  function quantityByPoNumber(purchaseOrders: { type?: string; poNumber: string | null; quantity: number }[]): Record<string, number> {
    return purchaseOrders.reduce<Record<string, number>>((result, po) => {
      if (po.type === ASSIGNMENT_TYPE.manual || !po.poNumber) return result;
      result[po.poNumber] = (result[po.poNumber] ?? 0) + po.quantity;
      return result;
    }, {});
  }

  // What each PO already consumes in the allocation the picker was opened on. Re-selecting
  // one of these resizes that row rather than adding a second, so only the difference
  // against this figure moves the week's remaining capacity.
  const assignedQuantities = computed<Record<string, number>>(() => quantityByPoNumber(calendarStore.allocations[context().rowId]?.[context().weekKey]?.purchaseOrders ?? []));

  function assignedQuantityFor(poNumber: string): number {
    return assignedQuantities.value[poNumber] ?? 0;
  }

  // A PO already on this allocation is edited in place: selecting it pre-fills its current
  // quantity and submitting overwrites the row (the backend treats a repeat assignment of
  // the same poNumber as a resize). Falls back to the caller-supplied list for contexts
  // whose allocation is not in the calendar store.
  function isResize(poNumber: string): boolean {
    return poNumber in assignedQuantities.value || context().assignedPoNumbers.includes(poNumber);
  }

  function assignedQuantityIn(capacityTypeId: string, poNumber: string): number {
    if (capacityTypeId === context().capacityTypeId) return assignedQuantityFor(poNumber);
    return weekAllocationsByTypeId.value[capacityTypeId]?.assignedQuantityByPoNumber[poNumber] ?? 0;
  }

  function typeCodeForId(capacityTypeId: string): string {
    return typeIdToCode.value[capacityTypeId] ?? (capacityTypeId === context().capacityTypeId ? context().typeCode : capacityTypeId);
  }

  // A PO is multi-type when it has 2+ parsed termination totals. Anything
  // else (single-type, estimated, or manual) uses the existing
  // single-quantity flow. Whether every type can actually be allocated is
  // checked separately by missingTypeCodesFor, which blocks selection.
  function isMultiType(po: PurchaseOrder): boolean {
    return Object.keys(po.terminationTotals).length >= 2;
  }

  // Type codes a multi-type PO needs that cannot be allocated this week —
  // either the vendor has no type mapping for the code at all (never
  // configured for this vendor) or a sibling row exists but has no planned
  // allocation this week. Either case blocks selection (checkbox disabled)
  // per the spec's missing rule.
  function missingTypeCodesFor(po: PurchaseOrder): string[] {
    if (!isMultiType(po)) return [];
    return Object.keys(po.terminationTotals).filter((code) => {
      const capacityTypeId = codeToTypeId.value[code];
      return !capacityTypeId || !(capacityTypeId in weekAllocationsByTypeId.value);
    });
  }

  // Type codes of a multi-type PO that have nothing left to assign — either
  // this PO's number is already listed in that type's current-week
  // allocation (weekAllocationsByTypeId[...].assignedPoNumbers, the most
  // direct "already handled this week" signal), or the type's parsed total
  // is already fully consumed by this PO's recorded assignments. Distinct
  // from missingTypeCodesFor, which flags types with nowhere to distribute
  // into at all; this flags types with somewhere to go but nothing left to
  // put there. Used to trim dead (zero-value) inputs out of a multi-type
  // selection, and to detect when a PO has no open type left at all.
  function assignedTypeCodesFor(po: PurchaseOrder): string[] {
    if (!isMultiType(po)) return [];
    return Object.keys(po.terminationTotals).filter((code) => {
      const capacityTypeId = codeToTypeId.value[code];
      if (!capacityTypeId) return false;
      if (weekAllocationsByTypeId.value[capacityTypeId]?.assignedPoNumbers.includes(po.poNumber)) return true;
      const assignedSameType = po.assignments.filter((assignment) => assignment.capacityTypeId === capacityTypeId).reduce((total, assignment) => total + assignment.quantity, 0);
      return Math.max((po.terminationTotals[code] ?? 0) - assignedSameType, 0) === 0;
    });
  }

  // True when every one of a multi-type PO's types has somewhere to go (none
  // missing) but nothing left to put there (all already assigned) — i.e.
  // this PO is fully handled for this week already. Kept distinct from the
  // missing-allocation case (a different reason, reported separately by
  // missingTypeCodesFor) so the two never blur into one ambiguous message.
  // Selection must be refused outright rather than adding a dead, all-zero
  // entry to `selected`.
  function isFullyAssignedFor(po: PurchaseOrder): boolean {
    if (!isMultiType(po)) return false;
    const codes = Object.keys(po.terminationTotals);
    if (codes.length === 0 || missingTypeCodesFor(po).length > 0) return false;
    const assigned = new Set(assignedTypeCodesFor(po));
    return codes.every((code) => assigned.has(code));
  }

  const selectedCount = computed(() => Object.keys(selected.value).length);
  // Net against what is already booked: a resize only spends the difference, so a PO left at
  // its current quantity costs nothing and shrinking one gives capacity back.
  const selectedTotal = computed(() =>
    Object.entries(selected.value).reduce(
      (total, [poNumber, portions]) =>
        total +
        Object.entries(portions).reduce((sum, [capacityTypeId, quantity]) => sum + (Number.isFinite(quantity) ? quantity : 0) - assignedQuantityIn(capacityTypeId, poNumber), 0),
      0
    )
  );
  const overCapacity = computed(() => selectedTotal.value > context().remainingCapacity);
  // A zero portion in a multi-type selection means "skip this type this week" and is
  // fine as long as another portion stays positive; an all-zero selection is only the
  // deliberate planned-at-0 flow (single portion on a zero-allowed PO).
  const canSubmit = computed(
    () =>
      selectedCount.value > 0 &&
      Object.entries(selected.value).every(([poNumber, portions]) => {
        const quantities = Object.values(portions);
        if (quantities.length === 0 || !quantities.every((quantity) => Number.isFinite(quantity) && quantity >= 0)) return false;
        if (quantities.some((quantity) => quantity > 0)) return true;
        return quantities.length === 1 && allowsZeroQuantity(poNumber);
      })
  );

  const canSubmitManual = computed(
    () => typeof manualQuantity.value === 'number' && Number.isFinite(manualQuantity.value) && manualQuantity.value > 0 && (!!manualPoNumber.value || !!manualNotes.value.trim())
  );

  // Submit gate: sum every selected PO's quantity per capacity type and
  // compare against that type's remaining capacity for the week. Includes the
  // context type for single-type overbooks even when no sibling rows loaded.
  const overbookedTypes = computed<OverbookedType[]>(() => {
    const sums: Record<string, number> = {};
    Object.entries(selected.value).forEach(([poNumber, portions]) => {
      Object.entries(portions).forEach(([capacityTypeId, quantity]) => {
        const net = (Number.isFinite(quantity) ? quantity : 0) - assignedQuantityIn(capacityTypeId, poNumber);
        sums[capacityTypeId] = (sums[capacityTypeId] ?? 0) + net;
      });
    });

    return Object.entries(sums).reduce<OverbookedType[]>((result, [capacityTypeId, total]) => {
      const remaining = weekAllocationsByTypeId.value[capacityTypeId]?.remainedCapacity ?? (capacityTypeId === context().capacityTypeId ? context().remainingCapacity : undefined);
      if (remaining === undefined || total <= remaining) return result;
      result.push({ typeCode: typeCodeForId(capacityTypeId), by: total - remaining });
      return result;
    }, []);
  });

  // A PO is offered for this week's termination type unless it is a known
  // mismatch: parsed counts exist but none for this type, no fiber-count
  // estimate, and no line of unknown type (unparsed without fallback data).
  const visibleItems = computed(() =>
    poStore.items.filter((po) => {
      const matchesType = (po.terminationTotals[context().typeCode] ?? 0) > 0;
      const hasEstimate = po.estimatedTotal > 0;
      const hasUnknownLine = po.lines.some((line) => (line.countsPerUnit === null && line.countSource === null) || line.unmappedConnectorCodes.length > 0);
      return matchesType || hasEstimate || hasUnknownLine || !po.isCapacityRelevant;
    })
  );
  const hiddenCount = computed(() => poStore.items.length - visibleItems.value.length);

  // The unified single-select dropdown offers only POs with something left to
  // schedule. POs whose totals are unknown (nothing parsed, no estimate) stay
  // offered — 0 there means "unknown", not "fully scheduled". Molding targets
  // default every PO to 0 by design, so the rule would empty their list.
  // Tools/raw-materials POs never consume capacity — the backend already drops
  // them from schedulable loads; this keeps stale or unfiltered store contents out too.
  const schedulableItems = computed(() =>
    visibleItems.value.filter((po) => {
      if (!po.isCapacityRelevant) return false;
      if (context().category === 'molding') return true;
      if (isMultiType(po)) return !isFullyAssignedFor(po);
      const knownTotal = (po.terminationTotals[context().typeCode] ?? 0) + po.estimatedTotal;
      return knownTotal === 0 || defaultQuantity(po) > 0;
    })
  );

  // Single-select counterpart to toggle(): the dropdown replaces the whole
  // selection on every change instead of accumulating entries.
  function selectExclusive(po: PurchaseOrder | null): void {
    if (!po || !isSelectable(po)) {
      selected.value = {};
      return;
    }
    selected.value = { [po.poNumber]: buildSelectionEntry(po) };
  }

  function toggleState(state: string): void {
    poStore.states = poStore.states.includes(state) ? poStore.states.filter((candidate) => candidate !== state) : [...poStore.states, state];
    if (poStore.states.length === 0) {
      poStore.states = [state];
      return;
    }
    reload();
  }

  // Default = this type's calculated total, plus the type-agnostic fiber-count
  // estimate, minus what is already assigned to same-type allocations. A molding
  // target always pre-fills 0 — molding has no parsed counts, so any parsed total
  // or fiber-count estimate present belongs to a different (termination) reading
  // of this PO, not this allocation.
  function defaultQuantity(po: PurchaseOrder): number {
    if (context().category === 'molding') return 0;
    if (!po.isCapacityRelevant) return 0;
    // The server remaining already nets out assignments across all weeks; the
    // client math below only stands in when counts were estimated (null there).
    const serverRemaining = po.remainingTotals?.[context().typeCode];
    if (serverRemaining !== undefined) return Math.max(serverRemaining, 0);
    const matchingTotal = po.terminationTotals[context().typeCode] ?? 0;
    const assignedSameType = po.assignments
      .filter((assignment) => assignment.capacityTypeId === context().capacityTypeId)
      .reduce((total, assignment) => total + assignment.quantity, 0);
    return Math.max(matchingTotal + po.estimatedTotal - assignedSameType, 0);
  }

  // Termination type ids the user can actually schedule into this week: every
  // termination-category sibling row with a planned allocation, always including
  // the row the picker was opened on.
  function availableTypeIds(): string[] {
    const ids = new Set(
      vendorRows.value
        .filter((row) => row.category === 'termination' && row.capacityTypeId && row.capacityTypeId in weekAllocationsByTypeId.value)
        .map((row) => row.capacityTypeId as string)
    );
    ids.add(context().capacityTypeId);
    return [...ids];
  }

  // Every schedulable termination type gets an input, prefilled with the parsed
  // per-type remaining where known and 0 otherwise — part-number auto-categorization
  // is unreliable, so the user can always override by hand. Zero portions mean "skip
  // this type" and are dropped on submit. A molding target keeps the single
  // context-type entry: molding has no parsed counts to spread.
  function buildSelectionEntry(po: PurchaseOrder): Record<string, number> {
    // A PO already on this allocation is resized in the capacity type the picker was opened
    // on, pre-filled with what it currently holds there. Even a multi-type PO takes this
    // path: its portions in the other types are edited from their own cells.
    if (isResize(po.poNumber)) {
      return { [context().capacityTypeId]: assignedQuantityFor(po.poNumber) };
    }
    // Non-capacity POs and molding targets share the planned-at-0 affordance,
    // which canSubmit only honors for a single-entry selection.
    if (!isMultiType(po) && (context().category === 'molding' || !po.isCapacityRelevant)) {
      return { [context().capacityTypeId]: 0 };
    }
    // A type this PO already has an assignment in this week is edited from its own
    // cell (like the resize branch above), not re-seeded here — zero-filling it would
    // corrupt selectedTotal (it nets against the existing assignment) and invite a
    // second distribution over the split the user already made.
    const assignedCodes = new Set(assignedTypeCodesFor(po));
    const assignedTypeIds = new Set([...assignedCodes].map((code) => codeToTypeId.value[code]).filter((typeId): typeId is string => Boolean(typeId)));
    const entry: Record<string, number> = Object.fromEntries(
      availableTypeIds()
        .filter((typeId) => !assignedTypeIds.has(typeId))
        .map((typeId) => [typeId, 0])
    );
    if (!isMultiType(po)) {
      entry[context().capacityTypeId] = defaultQuantity(po);
      return entry;
    }
    Object.keys(po.terminationTotals)
      .filter((code) => !assignedCodes.has(code))
      .forEach((code) => {
        const capacityTypeId = codeToTypeId.value[code];
        if (!capacityTypeId || !(capacityTypeId in entry)) return;
        const assignedSameType = po.assignments
          .filter((assignment) => assignment.capacityTypeId === capacityTypeId)
          .reduce((total, assignment) => total + assignment.quantity, 0);
        entry[capacityTypeId] = Math.max((po.terminationTotals[code] ?? 0) - assignedSameType, 0);
      });
    return entry;
  }

  function toggle(po: PurchaseOrder): void {
    if (po.poNumber in selected.value) {
      selected.value = Object.fromEntries(Object.entries(selected.value).filter(([poNumber]) => poNumber !== po.poNumber));
      return;
    }
    if (!isSelectable(po)) return;
    selected.value = { ...selected.value, [po.poNumber]: buildSelectionEntry(po) };
  }

  // The missing-allocation and fully-assigned gates only apply to a PO being distributed
  // across types for the first time — a resize touches one existing row in one type, so
  // neither can block it.
  function isSelectable(po: PurchaseOrder): boolean {
    if (isResize(po.poNumber)) return true;
    return missingTypeCodesFor(po).length === 0 && !isFullyAssignedFor(po);
  }

  // Submit entry point for views: defers to an explicit overbook confirmation
  // when any involved type would exceed its remaining capacity this week.
  async function requestSubmit(): Promise<void> {
    if (!canSubmit.value) return;
    if (overbookedTypes.value.length > 0) {
      pendingOverbook.value = overbookedTypes.value;
      return;
    }
    await submitConfirmed();
  }

  // Called directly when there is no overbook, or by the view after the user
  // confirms the ConfirmDialog. Multi-type selections distribute per PO;
  // single-type selections batch into one assignment call, as before.
  async function submitConfirmed(): Promise<void> {
    if (!canSubmit.value) return;
    pendingOverbook.value = null;
    submitting.value = true;

    // Zero portions are "skip this type this week" — dropped before routing. An
    // all-zero selection survives untouched: that's the planned-at-0 flow.
    const entries = Object.entries(selected.value).map(([poNumber, portions]): [string, Record<string, number>] => {
      const positive = Object.entries(portions).filter(([, quantity]) => quantity > 0);
      return [poNumber, positive.length > 0 ? Object.fromEntries(positive) : portions];
    });
    // A single surviving portion still needs the distribution path when it belongs
    // to a sibling type — the single-assign path books onto the context row only.
    const requiresDistribution = (portions: Record<string, number>): boolean =>
      Object.keys(portions).length > 1 || (Object.keys(portions).length === 1 && !(context().capacityTypeId in portions));
    const multiTypeEntries = entries.filter(([, portions]) => requiresDistribution(portions));
    const singleTypeEntries = entries.filter(([, portions]) => !requiresDistribution(portions));
    // The unified flow's optional comment rides along on real PO assignments too.
    const notes = manualNotes.value.trim();

    const multiResults = await Promise.all(
      multiTypeEntries.map(async ([poNumber, portions]) => {
        const distributionPortions: DistributionPortion[] = Object.entries(portions).map(([capacityTypeId, quantity]) => ({
          capacityTypeId,
          quantity,
        }));
        const productCategory = poByNumber.value.get(poNumber)?.productCategory ?? undefined;
        const ok = await calendarStore.distributePurchaseOrder(context().vendorId, context().weekKey, poNumber, distributionPortions, notes || undefined, productCategory);
        return { poNumber, ok };
      })
    );

    let singleResults: { poNumber: string; ok: boolean }[] = [];
    if (singleTypeEntries.length > 0) {
      const assignments = singleTypeEntries.map(([poNumber, portions]) => {
        const productCategory = poByNumber.value.get(poNumber)?.productCategory;
        return {
          poNumber,
          quantity: Object.values(portions)[0] ?? 0,
          ...(productCategory ? { productCategory } : {}),
          ...(notes ? { notes } : {}),
        };
      });
      const ok = await calendarStore.assignPurchaseOrders(context().rowId, context().weekKey, assignments);
      singleResults = singleTypeEntries.map(([poNumber]) => ({ poNumber, ok }));
    }

    submitting.value = false;

    const results = [...multiResults, ...singleResults];
    if (results.every((result) => result.ok)) {
      selected.value = {};
      manualNotes.value = '';
      manualQuantity.value = null;
      manualPoNumber.value = '';
      poStore.search = '';
      onAssigned?.();
      return;
    }

    // Partial failure: keep only the POs that failed selected — the store
    // already surfaced the error, this just avoids silently dropping them.
    const failedPoNumbers = new Set(results.filter((result) => !result.ok).map((result) => result.poNumber));
    selected.value = Object.fromEntries(Object.entries(selected.value).filter(([poNumber]) => failedPoNumbers.has(poNumber)));
  }

  // A manual block may optionally reference a PO (structured tracking data), but it
  // still skips the real-PO machinery above entirely (no multi-type split, no
  // overbook-confirmation dialog reuse) — it's a separate, deliberately simpler path.
  async function submitManualBlock(): Promise<void> {
    if (!canSubmitManual.value || manualQuantity.value === null) return;
    submitting.value = true;

    const notes = manualNotes.value.trim();
    const ok = await calendarStore.assignPurchaseOrders(context().rowId, context().weekKey, [
      {
        type: ASSIGNMENT_TYPE.manual,
        quantity: manualQuantity.value,
        ...(manualPoNumber.value ? { poNumber: manualPoNumber.value } : {}),
        ...(notes ? { notes } : {}),
      },
    ]);

    submitting.value = false;
    if (ok) {
      manualQuantity.value = null;
      manualNotes.value = '';
      manualPoNumber.value = '';
      poStore.search = '';
      onAssigned?.();
    }
  }

  watch(
    () => poStore.search,
    () => {
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(() => reload(), SEARCH_DEBOUNCE_MS);
    }
  );

  // Prune selections that fall out of view when the filters change, so hidden
  // POs cannot silently ride along in the submit payload.
  watch(visibleItems, (items) => {
    const visible = new Set(items.map((po) => po.poNumber));
    selected.value = Object.fromEntries(Object.entries(selected.value).filter(([poNumber]) => visible.has(poNumber)));
  });

  // Same guard for the manual block's reference PO, which lists poStore.items directly:
  // a filter change that hides the selected row must not leave it silently submitted.
  watch(
    () => poStore.items,
    (items) => {
      if (!manualPoNumber.value) return;
      if (!items.some((po) => po.poNumber === manualPoNumber.value)) manualPoNumber.value = '';
    }
  );

  // Lifecycle hooks only bind when called inside a component's setup();
  // the unit tests call the composable bare.
  if (getCurrentInstance()) {
    // Re-load whenever the owning context resolves or switches vendors; skips
    // the empty fallback context a view may pass while its store is loading.
    watch(
      () => context().vendorId,
      (vendorId) => {
        if (!vendorId) {
          return;
        }
        poStore.reset();
        poStore.schedulableForType = context().capacityTypeId || undefined;
        void poStore.load(vendorId);
      },
      { immediate: true }
    );
    onBeforeUnmount(() => {
      if (searchTimer) clearTimeout(searchTimer);
    });
  }

  return {
    poStore,
    visibleItems,
    hiddenCount,
    schedulableItems,
    selectExclusive,
    reload,
    selected,
    submitting,
    selectedCount,
    selectedTotal,
    overCapacity,
    canSubmit,
    pendingOverbook,
    overbookedTypes,
    weekAllocationsByTypeId,
    isMultiType,
    missingTypeCodesFor,
    assignedTypeCodesFor,
    isFullyAssignedFor,
    isSelectable,
    isResize,
    assignedQuantityFor,
    allowsZeroQuantity,
    isNonCapacityPo,
    typeCodeForId,
    toggleState,
    toggle,
    defaultQuantity,
    requestSubmit,
    submitConfirmed,
    manualQuantity,
    manualNotes,
    manualPoNumber,
    canSubmitManual,
    submitManualBlock,
  };
}
